<template>
  <div class="flex min-h-screen items-center justify-center">
    <div class="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <Card
        v-if="!showOTP && !isProcessingLink && !linkError"
        class="w-full max-w-sm p-4 shadow-lg border border-border bg-card/90 backdrop-blur-md text-black"
      >
        <CardHeader class="space-y-1">
          <CardTitle class="text-2xl font-bold text-center">Welcome</CardTitle>
          <CardDescription class="text-center"
            >Sign in or create your account to get started</CardDescription
          >
        </CardHeader>
        <CardContent>
          <form class="space-y-4" @submit.prevent="handleEmailLogin">
            <div class="space-y-2">
              <Label for="email">Email</Label>
              <Input
                id="email"
                v-model="email"
                :class="{ 'border-destructive': emailError }"
                placeholder="name@example.com"
                type="email"
                autocomplete="email"
              />
              <p v-if="emailError" class="text-sm text-destructive">{{ emailError }}</p>
            </div>
            <Button class="w-full" type="submit" :disabled="isLoading">
              <span v-if="isLoading" class="mr-2">
                <Icon name="ci:loading" class="animate-spin" />
              </span>
              Continue with Email
            </Button>
          </form>
          <div class="relative my-4">
            <div class="absolute inset-0 flex items-center">
              <span class="w-full border-t"></span>
            </div>
            <div class="relative flex justify-center text-xs uppercase">
              <span class="bg-card px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <Button variant="outline" class="opacity-50 cursor-not-allowed" disabled>
              <Icon name="logos:google-icon" class="mr-2 h-4 w-4" />
              Google
            </Button>
            <Button variant="outline" class="opacity-50 cursor-not-allowed" disabled>
              <Icon name="logos:apple" class="mr-2 h-4 w-4" />
              Apple
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card
        v-else-if="showOTP"
        class="w-full max-w-sm p-4 shadow-lg border border-border bg-card/90 backdrop-blur-md text-black"
      >
        <CardHeader class="space-y-1">
          <CardTitle class="text-2xl font-bold text-center">Verify Your Email</CardTitle>
          <CardDescription class="text-center"
            >Enter the verification code sent to your email</CardDescription
          >
        </CardHeader>
        <CardContent>
          <form class="space-y-4" @submit.prevent="handleOTPSubmit">
            <div class="space-y-2">
              <Label for="otp">Verification Code</Label>
              <Input id="otp" v-model="otp" placeholder="Enter code" />
            </div>
            <Button class="w-full" type="submit" :disabled="isLoading">
              <span v-if="isLoading" class="mr-2">
                <Icon name="ci:loading" class="animate-spin" />
              </span>
              Verify
            </Button>
            <Button class="w-full" variant="outline" @click="resetAuth">Try Again</Button>
          </form>
        </CardContent>
      </Card>
      <Card
        v-else-if="isProcessingLink"
        class="w-full max-w-sm p-4 shadow-lg border border-border bg-card/90 backdrop-blur-md text-black"
      >
        <CardContent class="flex flex-col items-center justify-center h-[200px]">
          <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-primary" />
          <p class="mt-4 text-lg font-medium text-center">Processing your login link...</p>
        </CardContent>
      </Card>
      <Card
        v-else-if="linkError"
        class="w-full max-w-sm p-4 shadow-lg border border-border bg-card/90 backdrop-blur-md text-black"
      >
        <CardHeader>
          <CardTitle class="text-destructive text-center">Login Error</CardTitle>
        </CardHeader>
        <CardContent class="space-y-4">
          <p class="text-center">{{ linkError }}</p>
          <Button class="w-full" @click="resetAuth">Try Again</Button>
        </CardContent>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { createCode, consumeCode } from 'supertokens-web-js/recipe/passwordless'
import { useUserStore } from '@/stores/user'
import { usePageTitle } from '~/composables/usePageTitle'
import { useToast } from '@/components/ui/toast'
import { useLogger } from '~/composables/useLogger'
import { useRouter } from 'vue-router'
import { useColorMode, useNuxtApp } from '#imports'

const { toast } = useToast()
const logger = useLogger()
const nuxtApp = useNuxtApp()
const colorMode = useColorMode()
let originalColorPreference: string

usePageTitle({
  title: 'Sign In',
  description: 'Sign in or create your grep account to get started',
})

const email = ref('')
const emailError = ref('')
const otp = ref('')
const showOTP = ref(false)
const isProcessingLink = ref(false)
const linkError = ref<string | null>(null)
const userStore = useUserStore()
const isLoading = ref(false)
let removeRouteHook: (() => void) | null = null

function validateEmail(email: string) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(email)
}

async function handleAuth(type: 'email' | 'otp', value: string) {
  isLoading.value = true

  try {
    const response =
      type === 'email'
        ? await createCode({ email: value })
        : await consumeCode({ userInputCode: value })

    logger.log(`SuperTokens ${type} response:`, response)

    if (response.status === 'OK') {
      if (type === 'email') {
        showOTP.value = true
      } else {
        await nuxtApp.$loadInitialData()
        navigateTo('/dashboard/home')
      }
    } else {
      logger.logError(`SuperTokens ${type} failed:`, response)
      throw new Error(type === 'email' ? 'Failed to send code' : 'Invalid code')
    }
  } catch (error) {
    logger.logError(`Authentication error (${type}):`, error)
    toast({
      title: 'Error',
      description:
        error instanceof Error
          ? `Authentication failed: ${error.message}`
          : 'Authentication failed: Unknown error',
      variant: 'destructive',
    })
  } finally {
    isLoading.value = false
  }
}

function resetAuth() {
  showOTP.value = false
  email.value = ''
  otp.value = ''
  isProcessingLink.value = false
  linkError.value = null
}

// Handle magic link login
if (import.meta.client) {
  const url = new URL(window.location.href)
  const preAuthSessionId = url.searchParams.get('preAuthSessionId')
  if (preAuthSessionId) {
    handleMagicLinkLogin(preAuthSessionId)
  }
}

async function handleMagicLinkLogin(preAuthSessionId: string) {
  isProcessingLink.value = true
  try {
    const response = await consumeCode({
      userInputCode: preAuthSessionId,
    })
    if (response.status === 'OK') {
      await nuxtApp.$loadInitialData()
      navigateTo('/dashboard/home')
    } else {
      linkError.value = 'Invalid or expired login link. Please try again.'
    }
  } catch (err) {
    logError('Magic link login error:', err)
    linkError.value = 'An error occurred while processing your login link. Please try again.'
  } finally {
    isProcessingLink.value = false
  }
}

async function handleEmailLogin() {
  emailError.value = ''
  if (!validateEmail(email.value)) {
    emailError.value = 'Please enter a valid email address'
    return
  }
  await handleAuth('email', email.value)
}

async function handleOTPSubmit() {
  if (!otp.value) {
    toast({
      title: 'Error',
      description: 'Please enter the OTP code',
      variant: 'destructive',
    })
    return
  }
  await handleAuth('otp', otp.value)
}

// Add redirect check and disable scroll on mount
onMounted(async () => {
  // === Client-side setup: Run this BEFORE any async operations ===
  if (import.meta.client) {
    // --- Force Light Mode ---
    originalColorPreference = colorMode.preference
    colorMode.preference = 'light'
    // --- End Force Light Mode ---

    // Prevent default scrolling behavior on html and body for this page
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalBodyOverflow = document.body.style.overflow
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'

    // Store cleanup function
    const cleanup = () => {
      document.documentElement.style.overflow = originalHtmlOverflow // Reset html overflow
      document.body.style.overflow = originalBodyOverflow // Reset body overflow

      // --- Restore Color Mode ---
      if (originalColorPreference) {
        colorMode.preference = originalColorPreference
      }
      // --- End Restore Color Mode ---
    }

    // Register cleanup hook BEFORE await
    onUnmounted(() => {
      cleanup()
      // Also remove the navigation guard
      if (removeRouteHook) {
        removeRouteHook()
      }
    })

    // Setup route change cleanup BEFORE await
    const router = useRouter()
    // Assign the hook remover function returned by beforeEach
    removeRouteHook = router.beforeEach((to, from, next) => {
      // Cleanup only when navigating away from 'auth' page
      if (from.name === 'auth') {
        cleanup()
        // No need to remove the hook here, it's handled in onUnmounted
      }
      next()
    })
  }
  // === End of client-side setup ===

  // Now perform async checks
  const isLoggedIn = await userStore.checkLoginStatus()
  if (isLoggedIn) {
    navigateTo('/dashboard/home')
    return // Stop further execution if redirecting
  }

  // Disable scrolling only if not redirecting
  // if (import.meta.client) { ... }
})
</script>
